import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { ConnectBloggleModal } from '../-components/connect-bloggle-modal';
import { ConnectShopifyModal } from '../-components/connect-shopify-modal';
import { ConnectWordpressModal } from '../-components/connect-wordpress-modal';
import { DeleteIntegrationDialog } from '../-components/delete-integration-dialog';
import { EditIntegrationDialog } from '../-components/edit-integration-dialog';
import { ButtonSeeHowItWorks } from '../../../../../../components/button-see-how-it-works';
import { IntegrationCard } from '../../../../../../components/integration-card';
import { ToastVariant } from '../../../../../../components/toast';
import { IntegrationProvider } from '../../../../../../entity/api/organization-integration';
import { addTrackCrumb } from '../../../../../../error/tracking';
import {
  useDeleteOrganizationIntegration,
  useOrganizationIntegrations,
  useUpdateOrganizationIntegration,
} from '../../../../../../services/storage/organization-integrations-queries';
import { tNamespace } from '../../../../../../services/translation';
import { useToast } from '../../../../../../shared/contexts/toast-context';
import { Feature, isFeatureAvailable } from '../../../../../../shared/feature';

const t = tNamespace('app.integrations');

// Strips OAuth callback params from the URL without triggering a router navigation
function clearOAuthSearchParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete('success');
  url.searchParams.delete('error');
  window.history.replaceState({}, '', url.toString());
}

const integrationsSearchSchema = z.object({
  success: z.boolean().optional(),
  error: z.string().optional(),
});

const integrationsParamsSchema = z.object({ id: z.string() });

type IntegrationsSearch = z.infer<typeof integrationsSearchSchema>;

export const Route = createFileRoute(
  '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout/integrations'
)({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',

      message: '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout/integrations',
      level: 'info',
    });
  },

  validateSearch: function (search: Record<string, unknown>): IntegrationsSearch {
    return integrationsSearchSchema.parse({
      success: search.success === 'true' || search.success === true || undefined,
      error: typeof search.error === 'string' ? search.error : undefined,
    });
  },

  // eslint-disable-next-line no-use-before-define
  component: IntegrationsTab,
});

export function IntegrationsTab() {
  const { id } = integrationsParamsSchema.parse(Route.useParams());
  const { success, error } = integrationsSearchSchema.parse(Route.useSearch());
  const [isEditIntegrationModalOpen, setIsEditIntegrationModalOpen] = useState(false);
  const [isDeleteIntegrationModalOpen, setIsDeleteIntegrationModalOpen] = useState(false);
  const [editIntegrationId, setEditIntegrationId] = useState<string | null>(null);

  const { data: allIntegrations } = useOrganizationIntegrations(id);
  const isShopifyAvailable = isFeatureAvailable(Feature.SHOPIFY_INTEGRATION);
  const isBloggleAvailable = isFeatureAvailable(Feature.BLOGGLE_INTEGRATION);
  const shopifyIntegrations = allIntegrations?.filter(
    (i) => i.provider === IntegrationProvider.Shopify
  );
  const wordpressIntegrations = allIntegrations?.filter(
    (i) => i.provider === IntegrationProvider.Wordpress
  );
  const bloggleIntegrations = allIntegrations?.filter(
    (i) => i.provider === IntegrationProvider.Bloggle
  );

  const addToast = useToast();

  useEffect(
    function () {
      if (success === true) {
        addToast({ title: t('oauthConnected'), variant: ToastVariant.Success, id: Date.now() });
        clearOAuthSearchParams();
      } else if (error != null) {
        addToast({ title: t('oauthConnectError'), variant: ToastVariant.Error, id: Date.now() });
        clearOAuthSearchParams();
      }
    },
    // Run once on mount based on query params; allIntegrations intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const deleteIntegration = useDeleteOrganizationIntegration();
  const updateIntegration = useUpdateOrganizationIntegration();

  const visibleIntegrations = (allIntegrations ?? []).filter(
    (i) =>
      (isShopifyAvailable || i.provider !== IntegrationProvider.Shopify) &&
      (isBloggleAvailable || i.provider !== IntegrationProvider.Bloggle)
  );
  const hasIntegrations = visibleIntegrations.length > 0;

  const [isConnectShopifyModalOpen, setIsConnectShopifyModalOpen] = useState(false);
  const [isConnectWordpressModalOpen, setIsConnectWordpressModalOpen] = useState(false);
  const [isConnectBloggleModalOpen, setIsConnectBloggleModalOpen] = useState(false);

  const editIntegration = allIntegrations?.find(
    (integration) => integration._id === editIntegrationId
  );

  function handleOpenConnectShopifyModal() {
    setIsConnectShopifyModalOpen(true);
  }

  function handleCloseConnectShopifyModal() {
    setIsConnectShopifyModalOpen(false);
  }

  function handleOpenConnectWordpressModal() {
    setIsConnectWordpressModalOpen(true);
  }

  function handleCloseConnectWordpressModal() {
    setIsConnectWordpressModalOpen(false);
  }

  function handleOpenConnectBloggleModal() {
    setIsConnectBloggleModalOpen(true);
  }

  function handleCloseConnectBloggleModal() {
    setIsConnectBloggleModalOpen(false);
  }

  return (
    <>
      <ButtonSeeHowItWorks videoUrl="https://www.youtube.com/embed/7ZKwCAEp_Do" />
      {!hasIntegrations ? (
        <div className="flex flex-col items-center">
          <img src="/paper-plane-3.svg" alt="Integrations" width={230} />
          <p className="font-display-2 mb-4 mt-2 text-center">{t('noIntegrationsFound')}</p>
          <p className="font-body-2-r text-center">{t('noIntegrationsFoundDescription')}</p>
        </div>
      ) : null}
      <div className="mt-[40px] flex flex-col gap-6 pb-6">
        {hasIntegrations ? <p className="font-display-4">{t('integrations')}</p> : null}
        {isBloggleAvailable ? (
          <IntegrationCard
            icon={
              <>
                <img src="/bloggle-square.svg" alt="Bloggle" />
              </>
            }
            title={t('bloggle')}
            description={t('bloggleDescription')}
            accounts={bloggleIntegrations?.map(function (integration) {
              return {
                id: integration._id,
                name: String(integration.externalName ?? 'Bloggle'),
                url: String(integration.externalUrl ?? ''),
                status: integration.status,
              };
            })}
            onConnect={handleOpenConnectBloggleModal}
            onDisconnect={(integrationId: string) => {
              setEditIntegrationId(integrationId);
              setIsDeleteIntegrationModalOpen(true);
            }}
            onEdit={(integrationId: string) => {
              setEditIntegrationId(integrationId);
              setIsEditIntegrationModalOpen(true);
            }}
          />
        ) : null}
        {isShopifyAvailable ? (
          <IntegrationCard
            icon={<img src="/shopify.svg" alt="Shopify" />}
            title={t('shopify')}
            description={t('shopifyDescription')}
            accounts={shopifyIntegrations?.map(function (integration) {
              return {
                id: integration._id,
                name: String(integration.externalName ?? 'Shopify'),
                url: String(integration.externalUrl ?? ''),
                status: integration.status,
              };
            })}
            onConnect={handleOpenConnectShopifyModal}
            onDisconnect={(integrationId: string) => {
              setEditIntegrationId(integrationId);
              setIsDeleteIntegrationModalOpen(true);
            }}
            onEdit={(integrationId: string) => {
              setEditIntegrationId(integrationId);
              setIsEditIntegrationModalOpen(true);
            }}
          />
        ) : null}
        <IntegrationCard
          icon={<img src="/wordpress.svg" alt="WordPress" />}
          title={t('wordpress')}
          description={t('wordpressDescription')}
          accounts={wordpressIntegrations?.map(function (integration) {
            return {
              id: integration._id,
              name: String(integration.externalName ?? 'WordPress'),
              url: String(integration.externalUrl ?? ''),
              status: integration.status,
            };
          })}
          onConnect={handleOpenConnectWordpressModal}
          onDisconnect={(integrationId: string) => {
            setEditIntegrationId(integrationId);
            setIsDeleteIntegrationModalOpen(true);
          }}
          onEdit={(integrationId: string) => {
            setEditIntegrationId(integrationId);
            setIsEditIntegrationModalOpen(true);
          }}
        />
        {isShopifyAvailable ? (
          <ConnectShopifyModal
            open={isConnectShopifyModalOpen}
            onOpenChange={handleCloseConnectShopifyModal}
            organizationId={id}
          />
        ) : null}
        {isBloggleAvailable ? (
          <ConnectBloggleModal
            open={isConnectBloggleModalOpen}
            onOpenChange={handleCloseConnectBloggleModal}
            organizationId={id}
          />
        ) : null}
        <ConnectWordpressModal
          open={isConnectWordpressModalOpen}
          onOpenChange={handleCloseConnectWordpressModal}
          organizationId={id}
        />
        <EditIntegrationDialog
          open={isEditIntegrationModalOpen}
          onOpenChange={setIsEditIntegrationModalOpen}
          isLoading={updateIntegration.isPending}
          onSubmit={function (integrationName, integrationId) {
            updateIntegration.mutate(
              { organizationId: id, integrationId, data: { externalName: integrationName } },
              {
                onSuccess: function () {
                  setIsEditIntegrationModalOpen(false);
                  setEditIntegrationId(null);
                  addToast({
                    title: t('editSuccess'),
                    variant: ToastVariant.Success,
                    id: Date.now(),
                  });
                },
              }
            );
          }}
          integrationName={editIntegration?.externalName ?? ''}
          integrationId={editIntegration?._id ?? ''}
        />
        <DeleteIntegrationDialog
          open={isDeleteIntegrationModalOpen}
          onOpenChange={setIsDeleteIntegrationModalOpen}
          isLoading={deleteIntegration.isPending}
          onDelete={function (integrationId) {
            deleteIntegration.mutate(
              { organizationId: id, integrationId },
              {
                onSuccess: function () {
                  setIsDeleteIntegrationModalOpen(false);
                  setEditIntegrationId(null);
                  addToast({
                    title: t('disconnectSuccess'),
                    variant: ToastVariant.Success,
                    id: Date.now(),
                  });
                },
              }
            );
          }}
          integrationUrl={editIntegration?.externalUrl ?? editIntegration?.externalName ?? ''}
          integrationId={editIntegration?._id ?? ''}
        />
      </div>
    </>
  );
}
